import { Button, Icon, Toggle, PageTitle } from '@pwc/pwc-deals-ui';
import { get } from 'lodash';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@mui/material';

import { Constants } from '@pwc/pwc-deals-web-common';
import { CloseButton, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { TABLEAU_LIBRARY_ITEM_TYPES } from 'common/components/SectionArea/libraryItemUtils.ts';
import PowerBiReport from 'features/analyses/PowerBiReport/PowerBiReport';
import { calcContentHeight } from 'features/analyses/PowerBiReport/powerBiReportHelpers';
import {
  getTableauReportDetail,
  getTableauAutoSyncUrl,
} from '../../../features/analyses/tableauService.ts';
import { TableauPreview } from './TableauPreview.tsx';
import {
  convertToSingleTabEmbedUrl,
  getTabNameBySheetUrl,
} from './tableauUtils';
import ShowForRole from '../../../features/security/ShowForRole';
import styles from './SelectReportDetail.module.scss';

export const SELECT_REPORT_TYPE = {
  POWERBI: 'POWERBI',
  TABLEAU: 'TABLEAU',
};

const HEIGHT_BUFFER = 5;
const TABLEAU_HEIGHT = 500;

const useMaxModalHeight = ({ initialHeight } = {}) => {
  const [height, setHeight] = useState(initialHeight);

  const handleHeight = () => {
    setHeight(document.documentElement.clientHeight);
  };

  useEffect(() => {
    handleHeight();
    window.addEventListener('resize', handleHeight);
    return () => window.removeEventListener('resize', handleHeight);
  }, []);

  return {
    height,
  };
};

export const SelectReportDetail = ({
  show = true,
  onHide = () => {},
  onBack = () => {},
  onAdd = () => {},
  file,
  title,
  workspaceUrl,
  reportType,
  pageItemType,
  createReportLibraryItem,
  workspaceId,
}) => {
  const { t } = useTranslation();
  const ref = useRef();
  const { projectId } = useParams();
  const { height } = useMaxModalHeight();
  const [report, setReport] = useState(null);
  const powerBiUrl = report
    ? `${workspaceUrl}/${report.reportId}?workspaceId=${workspaceId}&typeId=POWERBI_VIZ&guid=${report.reportId}&type=REPORT`
    : workspaceUrl;
  const [tableauReport, setTableauReport] = useState(null);
  const [pageSelected, setPageSelected] = useState(null);
  const [allTabsSelected, setAllTabsToggle] = useState(false);
  const [elementToAdd, setElementToAdd] = useState(file);
  const [isReportRendered, setIsReportRendered] = useState(false);
  const [tableauInstance, setTableauInstance] = useState();
  const tableauAutoSyncUrlRef = useRef('');

  useEffect(() => {
    if (reportType === SELECT_REPORT_TYPE.TABLEAU) {
      getTableauAutoSyncUrl(projectId, workspaceId, file.id).then((data) => {
        tableauAutoSyncUrlRef.current = data.embedUrl;
      });
      getTableauReportDetail(projectId, workspaceId, file.id).then((data) => {
        setTableauReport({ ...data, name: file.name, reportId: file.id });
      });
    }
  }, [reportType, file, workspaceId, projectId]);

  const pageItem = useMemo(() => {
    const pageName = allTabsSelected ? '' : get(pageSelected, 'name', '');
    return (
      file && {
        ...file,
        data:
          report && pageSelected
            ? `{"ReportId":"${report.reportId}","PageName":"${pageName}", "EmbedUrl":"${report.embedUrl}", "EmbedToken":"${report.embedToken}"}`
            : file.data,
        pageName: pageSelected ? pageSelected.name : null,
        pageDisplayName: pageSelected ? pageSelected.displayName : null,
        type: allTabsSelected ? pageItemType.report : pageItemType.reportPage,
      }
    );
  }, [
    file,
    report,
    pageSelected,
    allTabsSelected,
    pageItemType.report,
    pageItemType.reportPage,
  ]);

  const reportItem = useMemo(() => file, [file]);

  useEffect(() => setElementToAdd(pageItem), [pageItem]);

  const handleTabs = () => {
    const areAllTabsSelected = !allTabsSelected;
    const selectedItem = allTabsSelected
      ? {
          ...file,
          type: pageItemType.report,
        }
      : {
          ...pageItem,
          type: pageItemType.reportPage,
        };

    setAllTabsToggle(areAllTabsSelected);
    setElementToAdd(selectedItem);
  };

  return (
    <Modal
      ref={ref}
      dialogClassName={styles['powerbi-detail-modal-dialog']}
      contentClassName={styles['powerbi-detail-modal-content']}
      show={show}
      backdrop={false}
      keyboard={false}
    >
      <div className={styles['powerbi-detail-modal-wrapper']}>
        <Modal.Header className={styles['powerbi-detail-modal-header']}>
          <Modal.Title>
            <Button
              className="button-menu"
              type="button"
              onClick={onBack}
              data-testid="back-button"
            >
              <Icon
                name="left-chevron"
                width={18}
                className="fill icon-orange"
              />
            </Button>
            <PageTitle title={title} size="h2" />
          </Modal.Title>
          <CloseButton onClick={onHide} />
        </Modal.Header>
        <div className={styles['powerbi-detail-modal-content']}>
          {height && (
            <>
              {reportType === SELECT_REPORT_TYPE.POWERBI && (
                <PowerBiReport
                  projectId={projectId}
                  item={reportItem}
                  height={calcContentHeight(height) - HEIGHT_BUFFER}
                  updateDimensions={false}
                  handleReportData={setReport}
                  handlePageSelect={setPageSelected}
                  handleRenderStatus={setIsReportRendered}
                  showAutoSync={false}
                />
              )}
              {reportType === SELECT_REPORT_TYPE.TABLEAU && (
                <TableauPreview
                  report={tableauReport}
                  setIsReportRendered={setIsReportRendered}
                  setTableauInstance={setTableauInstance}
                  showAutoSyncLink={false}
                  height={TABLEAU_HEIGHT}
                />
              )}
            </>
          )}
        </div>
        <Modal.Footer className={styles['powerbi-detail-modal-footer']}>
          <div className={styles['powerbi-detail-modal-actions']}>
            <ShowForRole
              projectId={projectId}
              roles={[
                Constants.USER_ROLES.ENGAGEMENT_LEAD,
                Constants.USER_ROLES.ENGAGEMENT_MANAGER,
                Constants.USER_ROLES.PROJECT_ADMIN,
                Constants.USER_ROLES.APPROVER,
                Constants.USER_ROLES.TEAM_MEMBER,
              ]}
            >
              <div className={styles['powerbi-detail-modal-actions-link']}>
                {reportType === SELECT_REPORT_TYPE.TABLEAU &&
                  tableauAutoSyncUrlRef.current && (
                    <p>
                      <span className="select-powerbi-modal__actions-source">
                        Source file link
                      </span>
                      <Link
                        className="select-powerbi-modal__actions-file"
                        href={`${tableauAutoSyncUrlRef.current}?web=1`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {file.name}
                      </Link>
                    </p>
                  )}
                {reportType !== SELECT_REPORT_TYPE.TABLEAU && (
                  <p>
                    <span className="select-powerbi-modal__actions-source">
                      {t('sourceFileLink')}
                    </span>
                    <Link
                      className="select-powerbi-modal__actions-file"
                      href={powerBiUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {file.name}
                    </Link>
                  </p>
                )}
              </div>
            </ShowForRole>
            <div className={styles['powerbi-detail-modal-actions-buttons']}>
              <div className={styles['powerbi-detail-modal-actions-checkbox']}>
                <Toggle
                  checked={allTabsSelected}
                  onClick={handleTabs}
                  name="powerbi-select-all"
                  dataTestId="powerbi-modal-select-all-tabs"
                  disabled={!isReportRendered}
                >
                  {t('importAllTabs')}
                </Toggle>
              </div>
              <Button
                className="btn-large"
                type="button"
                disabled={
                  !isReportRendered ||
                  (reportType === SELECT_REPORT_TYPE.TABLEAU &&
                    !tableauInstance)
                }
                onClick={async () => {
                  if (reportType === SELECT_REPORT_TYPE.POWERBI) {
                    const libraryItemId = await createReportLibraryItem(
                      elementToAdd,
                      allTabsSelected
                    );
                    const libraryItemCreated = {
                      ...elementToAdd,
                      id: libraryItemId,
                    };
                    if (allTabsSelected) {
                      libraryItemCreated.pageName = '';
                    }
                    if (allTabsSelected) {
                      libraryItemCreated.pageName = '';
                    }
                    onAdd(libraryItemCreated);
                  } else if (reportType === SELECT_REPORT_TYPE.TABLEAU) {
                    const workbook = tableauInstance.getWorkbook();
                    if (workbook) {
                      const activeSheet = workbook.getActiveSheet();
                      const activeSheetUrl = activeSheet.getUrl();
                      const sheetUrlTemp = activeSheetUrl.split('/');
                      sheetUrlTemp.pop();
                      const tabName = allTabsSelected
                        ? getTabNameBySheetUrl(tableauReport.embedUrl)
                        : getTabNameBySheetUrl(activeSheetUrl);
                      // eslint-disable-next-line no-underscore-dangle
                      const tabDisplayName = activeSheet._impl.$5;
                      const sheetUrl = allTabsSelected
                        ? `${sheetUrlTemp.join('/')}/${tabName}`
                        : activeSheetUrl;
                      let type = null;
                      let data = '';
                      let url = tableauReport.embedUrl;
                      if (allTabsSelected) {
                        type = TABLEAU_LIBRARY_ITEM_TYPES.TableauReport;
                        data = `{"ReportId":"${file.id}","Name":"${file.name}", "EmbedUrl":"${tableauReport.embedUrl}"}`;
                      } else {
                        url = convertToSingleTabEmbedUrl(
                          tableauReport.embedUrl,
                          tabName
                        );
                        type = TABLEAU_LIBRARY_ITEM_TYPES.TableauReportPage;
                        data = `{"ReportId":"${file.id}","TabName":"${tabName}", "EmbedUrl":"${url}"}`;
                      }

                      const itemToAdd = {
                        reportId: file.id,
                        name: file.name,
                        embedUrl: url,
                        data,
                        allTabsSelected,
                        sheetUrl,
                        tabName,
                        tabDisplayName,
                      };
                      const libraryItemId = await createReportLibraryItem(
                        { ...itemToAdd, tabName: tabDisplayName },
                        allTabsSelected,
                        file.id
                      );
                      onAdd({
                        ...itemToAdd,
                        libraryItemId,
                        id: libraryItemId,
                        type,
                      });
                    }
                  }
                }}
              >
                {t('addPage')}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </div>
    </Modal>
  );
};
